import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import SEO from '../components/SEO';
import AdComponent from '../components/AdComponent';
import HilltopBanner from '../components/HilltopBanner';

function CalculatorsListing() {
  const [calculators, setCalculators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [calculatorsPerPage] = useState(6); // 6 calculators per page

  useEffect(() => {
    const fetchCalculators = async () => {
      try {
        const response = await apiService.getCalculators();
        if (response.success) {
          setCalculators(response.data);
        }
      } catch (error) {
        console.error('Error fetching calculators:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCalculators();
  }, []);

  // Calculate pagination
  const indexOfLastCalculator = currentPage * calculatorsPerPage;
  const indexOfFirstCalculator = indexOfLastCalculator - calculatorsPerPage;
  const currentCalculators = calculators.slice(indexOfFirstCalculator, indexOfLastCalculator);
  const totalPages = Math.ceil(calculators.length / calculatorsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-cyan-50 py-12">
      <SEO
          title="CalcBlog - Calculators"
          description="Your destination for helpful calculators and insightful articles. Making complex calculations simple and accessible."
          keywords="calcblog, online calculators, health calculators, bmi calculator, calorie calculator, sleep optimization calculator, carbon footprint calculator, monthly budget calculator, personal finance calculator, sustainability calculators, exosome skincare calculator, ai powered learning tools, interactive calculators, free online calculators, educational calculators, calculator blog, best calculators online, calculator for students, calculator for health, calculator for finance, calculator for environment"
          toolType="calculator"
       />

      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-800 mb-4 dark:text-slate-200">
            All Calculators
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto dark:text-slate-400">
            Explore our collection of helpful calculators for various needs
          </p>
        </div>

        {/* Calculators Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {currentCalculators.map(calculator => (
            <Link 
              key={calculator.id} 
              to={`/calculators/${calculator.slug}`}
              className="calculator-card group text-center hover:shadow-xl transition-all duration-300 group"
            >
              <div className="w-20 h-20 bg-gradient-to-r from-orange-400 to-cyan-400 rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 dark:from-orange-500 dark:to-cyan-500">
                <span className="text-white text-2xl font-bold">∑</span>
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-4 dark:text-slate-200">
                {calculator.name}
              </h3>
              <p className="text-slate-600 mb-4 dark:text-slate-400 dark:text-slate-400">
                {calculator.description}
              </p>
              <div className="inline-flex items-center text-orange-500 font-semibold group-hover:translate-x-1 transition-transform duration-300">
                Use Calculator
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center space-x-2">
            {/* Previous Button */}
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                currentPage === 1
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-orange-500 text-white hover:bg-orange-600'
              }`}
            >
              ← Previous
            </button>

            {/* Page Numbers */}
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
              <button
                key={number}
                onClick={() => paginate(number)}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  currentPage === number
                    ? 'bg-orange-500 text-white shadow-lg'
                    : 'bg-white text-slate-700 hover:bg-orange-100 border border-slate-200'
                }`}
              >
                {number}
              </button>
            ))}

            {/* Next Button */}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                currentPage === totalPages
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-orange-500 text-white hover:bg-orange-600'
              }`}
            >
              Next →
            </button>
          </div>
        )}

        {/* Calculators Count */}
        <div className="text-center mt-8">
          <p className="text-slate-600">
            Showing {indexOfFirstCalculator + 1}-{Math.min(indexOfLastCalculator, calculators.length)} of {calculators.length} calculators
          </p>
          
          <div className="h-64 flex flex-1 mt-8 justify-center sm:pb-10">
                <HilltopBanner />
               { /*<div className="h-64"><AdComponent /></div>*/}
           </div>  
        </div>
        

        {/* Empty State */}
        {calculators.length === 0 && (
          <div className="text-center py-12">
            <div className="text-slate-400 text-6xl mb-4">🧮</div>
            <p className="text-slate-500 text-lg">No calculators available yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CalculatorsListing;